import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { CustomModalLayout, Modal } from 'wix-style-react';
import { API_INSTANCE } from 'src/features/utils';
import { selectSiteData } from 'src/features/account-slice';
import { Copy } from 'src/modules/events/components/copy';
import { useAppSelector, useAppDispatch } from 'src/hooks';
import { accountSelector } from 'src/redesign/features/Account/store/selectors';
import { TabView, TabPanel } from 'primereact/tabview';
import { RedesignButton } from 'src/redesign/components/RedesignButton';
import { OverlayPanel } from 'primereact/overlaypanel';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputOtp } from 'primereact/inputotp';
import { SocialInput } from './SocialInput';
import { sendPinCode, verifyPinCode } from 'src/redesign/pages/Login/store/actions';
import { codeIdSelector } from 'src/redesign/pages/Login/store/selectors';
import { setLoginEmailAction } from 'src/redesign/pages/Login/store/reducer';
import { uploadAvatar } from 'src/redesign/features/Account/store/actions';
import styles from './AccountSettingsModal.module.scss';
import { showNotification } from '../../Toast/store/actions';
import { UserCheck } from 'lucide-react';
import {
  InvisibleRecaptcha,
  InvisibleRecaptchaHandle,
} from 'src/redesign/components/InvisibleRecaptcha';

type AccountFormData = {
  first_name: string;
  last_name: string;
  bio: string;
  personal_website_url: string;
  linkedin_profile_url: string;
  youtube_profile_url: string;
  instagram_profile_url: string;
  x_profile_url: string;
};

type AccountSettingsModalProps = {
  isVisible?: boolean;
  setVisible: (value: boolean) => void;
};

// Password Change Section Component
const PasswordChangeSection: FC<{ email: string }> = ({ email }) => {
  const [newPassword, setNewPassword] = useState('');
  const [newConfirmPassword, setNewConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pinCode, setPinCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [cooldownActive, setCooldownActive] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(20);
  const cooldownTimerRef = useRef(null);
  const pinOverlay = useRef(null);
  const dispatch = useAppDispatch();
  const site = useSelector(selectSiteData);
  const codeID = useAppSelector(codeIdSelector);
  const recaptchaRef = useRef<InvisibleRecaptchaHandle>(null);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);

  const sendCode = useCallback(async () => {
    if (!recaptchaLoaded) {
      setError('Security verification is loading. Please wait.');
      return;
    }

    try {
      // Execute invisible reCAPTCHA
      const captchaToken = await recaptchaRef.current?.execute();

      if (!captchaToken) {
        setError('Failed to verify reCAPTCHA. Please try again.');
        return;
      }

      dispatch(setLoginEmailAction(email));
      dispatch(sendPinCode({ site, email, recaptchaToken: captchaToken }));
      setCooldownActive(true);
      setCooldownTime(20);
      setError(null); // Clear any errors when resending

      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }

      cooldownTimerRef.current = setInterval(() => {
        setCooldownTime((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownTimerRef.current);
            setCooldownActive(false);
            return 20;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err) {
      console.error('Error sending PIN code:', err);
      setError('Failed to send code. Please try again.');
      recaptchaRef.current?.reset();
    }
  }, [dispatch, email, site, recaptchaLoaded]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  const handleChangePasswordClick = (e) => {
    // Validate passwords
    if (!newPassword || !newConfirmPassword) {
      setError('Please enter both password fields');
      return;
    }
    if (newPassword !== newConfirmPassword) {
      setError('The new passwords do not match. Please try again.');
      return;
    }
    if (newPassword.length <= 8) {
      setError('The password must be longer than 8 characters.');
      return;
    }

    setError(null);
    setPinCode(''); // Clear any previous PIN

    // Show PIN overlay and send PIN code
    pinOverlay.current.toggle(e);
    sendCode();
  };

  const verifyCodeAndChangePassword = useCallback(async () => {
    if (pinCode.length !== 6) {
      return;
    }

    setVerifying(true);

    try {
      // Execute invisible reCAPTCHA for verification
      const captchaToken = await recaptchaRef.current?.execute();

      if (!captchaToken) {
        setError('Failed to verify reCAPTCHA. Please try again.');
        setVerifying(false);
        return;
      }

      // Step 1: Verify PIN code to get verificationCode
      const { data } = await API_INSTANCE.post(
        `/api/v1/dashboard/accounts/change-password-code-verification`,
        {
          email,
          code_verification: { code: pinCode, id: codeID },
          'g-recaptcha-response': captchaToken,
        },
      );

      const verificationCode = data.verificationCode;
      const captchaTokenVerify = await recaptchaRef.current?.execute();

      // Step 2: Call change-password with the verificationCode
      await API_INSTANCE.post(`/api/v1/dashboard/accounts/change-password`, {
        new_password: newPassword,
        new_password_confirm: newConfirmPassword,
        verificationCode: verificationCode,
        'g-recaptcha-response': captchaTokenVerify,
      });

      // Success - clear form and close overlay
      setNewPassword('');
      setNewConfirmPassword('');
      setPinCode('');
      setError(null);
      pinOverlay.current.hide();

      dispatch(
        showNotification({
          severity: 'success',
          summary: 'Success',
          detail: 'Your password has been changed successfully.',
          life: 3000,
        }),
      );
    } catch (err) {
      console.error(err);
      setPinCode(''); // Clear PIN on error to allow retry
      recaptchaRef.current?.reset();
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('Failed to verify code. Please try again.');
      }
    } finally {
      setVerifying(false);
    }
  }, [email, codeID, pinCode, newPassword, newConfirmPassword, dispatch]);

  return (
    <div className={styles.passwordSection}>
      <div className={styles.sectionTitle}>Change Password</div>

      <div className={styles.passwordGrid}>
        <div className={styles.formGroup}>
          <label className={styles.label}>New Password</label>
          <InputText
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="Enter new password"
            className={styles.input}
          />
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label}>Confirm New Password</label>
          <InputText
            type="password"
            value={newConfirmPassword}
            onChange={(e) => setNewConfirmPassword(e.target.value)}
            placeholder="Confirm new password"
            className={styles.input}
          />
        </div>
      </div>

      {error && <div className={styles.errorText}>{error}</div>}

      <RedesignButton
        label="Change Password"
        onClick={handleChangePasswordClick}
        variation="primary"
        disabled={!newPassword || !newConfirmPassword || !recaptchaLoaded}
      />

      {/* Invisible ReCAPTCHA */}
      <InvisibleRecaptcha
        ref={recaptchaRef}
        onLoad={() => setRecaptchaLoaded(true)}
        onVerify={(token) => {
          console.log('reCAPTCHA verified:', token);
        }}
        onExpired={() => {
          console.log('reCAPTCHA expired');
        }}
        onError={() => {
          console.error('reCAPTCHA error');
        }}
      />

      <OverlayPanel ref={pinOverlay} className={styles.pinOverlay}>
        <div className={styles.pinVerificationContent}>
          <h3 className={styles.pinTitle}>Verify Your Email</h3>
          <p className={styles.pinSubtitle}>
            Enter the 6-digit code sent to <strong>{email}</strong>
          </p>

          <InputOtp
            value={pinCode}
            onChange={(e) => setPinCode(e.value)}
            length={6}
            className={styles.inputOtp}
            disabled={verifying}
          />

          {verifying && <p className={styles.verifyingMessage}>Verifying...</p>}
          {error && <div className={styles.pinErrorText}>{error}</div>}

          <div className={styles.pinButtonWrapper}>
            {cooldownActive ? (
              <RedesignButton
                text
                label={`Resend in ${cooldownTime}s`}
                disabled
                className={styles.resendButton}
              />
            ) : (
              <RedesignButton
                text
                label="Resend Code"
                onClick={sendCode}
                className={styles.resendButton}
              />
            )}
          </div>

          <RedesignButton
            label="Submit Code"
            onClick={verifyCodeAndChangePassword}
            disabled={verifying || pinCode.length !== 6}
            variation="primary"
            className={styles.submitButton}
          />
        </div>
      </OverlayPanel>
    </div>
  );
};

export const AccountSettingsModal: FC<AccountSettingsModalProps> = ({ isVisible, setVisible }) => {
  const [loading, setLoading] = useState(false);
  const site = useSelector(selectSiteData);
  const account = useAppSelector(accountSelector);
  const dispatch = useAppDispatch();

  const { handleSubmit, control } = useForm<AccountFormData>({
    defaultValues: {
      first_name: '',
      last_name: '',
      handle: '',
      personal_website_url: '',
      display_name: '',
      linkedin_profile_url: '',
      youtube_profile_url: '',
      ...account,
    },
  });

  const closeModal = () => {
    setVisible(false);
  };

  const onSubmit: SubmitHandler<AccountFormData> = async (data) => {
    setLoading(true);

    try {
      const newData = data;
      await API_INSTANCE.put(`/api/v1/dashboard/accounts`, newData);

      dispatch(
        showNotification({
          severity: 'success',
          summary: 'Update Account',
          detail: 'Successfully Saved Changes',
          life: 3000,
        }),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to save changes',
          life: 3000,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const renderForm = () => (
    <div className={styles.tabContainer}>
      <TabView className={styles.tabView}>
        {/* Profile Tab */}
        <TabPanel header="Profile">
          <div className={styles.tabContent}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <ProfileForm control={control} />
              <div className={styles.buttonGroup}>
                <RedesignButton
                  type="submit"
                  variation="primary"
                  loading={loading}
                  size="large"
                  className={styles.saveButton}
                >
                  <div className={styles.saveButtonContent}>
                    <UserCheck size={20} />
                    <span>Save Changes</span>
                  </div>
                </RedesignButton>
              </div>
            </form>
          </div>
        </TabPanel>

        {/* Security Tab */}
        <TabPanel header="Security">
          <div className={styles.tabContent}>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Email Address</div>
              <div className={styles.emailDisplay}>{account?.email}</div>
              <div className={styles.helperText}>Your email address cannot be changed</div>
            </div>

            <div className={styles.divider} />

            <PasswordChangeSection email={account?.email} />
          </div>
        </TabPanel>

        {/* Referral Tab */}
        <TabPanel header="Referral Program">
          <div className={styles.tabContent}>
            <div className={styles.section}>
              <div className={styles.sectionTitle}>Referral Link</div>
              <div className={styles.sectionDescription}>
                Earn a 20% recurring commission for each subscription initiated through your unique
                referral link.
              </div>
              <Copy
                text={`https://ticketspotapp.com/ref/${account?.referral_name || 'your-ref-code'}`}
              />
            </div>
          </div>
        </TabPanel>
      </TabView>
    </div>
  );

  const renderModalContent = () => (
    <CustomModalLayout
      secondaryButtonText="Close"
      onCloseButtonClick={closeModal}
      secondaryButtonOnClick={closeModal}
      title={`Account Settings`}
      width="900px"
      height="80vh"
      removeContentPadding
    >
      {renderForm()}
    </CustomModalLayout>
  );

  return (
    <>
      <Modal isOpen={isVisible} onRequestClose={closeModal} screen="desktop">
        {renderModalContent()}
      </Modal>
    </>
  );
};

const ProfileForm: FC<{ control: any }> = ({ control }) => {
  const dispatch = useAppDispatch();
  const account = useAppSelector(accountSelector);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Please upload an image file',
          life: 3000,
        }),
      );
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Image must be less than 5MB',
          life: 3000,
        }),
      );
      return;
    }

    setUploading(true);
    try {
      await dispatch(uploadAvatar(file)).unwrap();
      dispatch(
        showNotification({
          severity: 'success',
          summary: 'Success',
          detail: 'Avatar uploaded successfully',
          life: 3000,
        }),
      );
    } catch (error) {
      console.error('Avatar upload error:', error);
      dispatch(
        showNotification({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to upload avatar',
          life: 3000,
        }),
      );
    } finally {
      setUploading(false);
    }
  };

  const avatarUrl = account?.avatar_url || account?.default_avatar_url;

  return (
    <div className={styles.profileContainer}>
      <div className={styles.profileHeader}>
        <div className={styles.sectionTitle}>Your Profile</div>
        <div className={styles.sectionSubtitle}>
          Choose how you are displayed as a host or guest.
        </div>
      </div>

      <div className={styles.profileLayout}>
        <div className={styles.profileFields}>
          <div className={styles.nameRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>First Name</label>
              <Controller
                control={control}
                name="first_name"
                render={({ field }) => <InputText {...field} className={styles.input} />}
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>Last Name</label>
              <Controller
                control={control}
                name="last_name"
                render={({ field }) => <InputText {...field} className={styles.input} />}
              />
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>Bio</label>
            <Controller
              control={control}
              name="bio"
              render={({ field }) => (
                <InputTextarea
                  {...field}
                  rows={3}
                  className={styles.textarea}
                  placeholder="Share a little about your background and interests."
                />
              )}
            />
          </div>

          <div className={styles.sectionTitle} style={{ marginTop: '24px' }}>
            Social Links
          </div>

          <div className={styles.socialLinksGrid}>
            <Controller
              control={control}
              name="instagram_profile_url"
              render={({ field }) => (
                <SocialInput
                  icon="pi pi-instagram"
                  prefix="instagram.com/"
                  placeholder="username"
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              control={control}
              name="x_profile_url"
              render={({ field }) => (
                <SocialInput
                  icon="pi pi-twitter"
                  prefix="x.com/"
                  placeholder="username"
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              control={control}
              name="youtube_profile_url"
              render={({ field }) => (
                <SocialInput
                  icon="pi pi-youtube"
                  prefix="youtube.com/@"
                  placeholder="username"
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              control={control}
              name="linkedin_profile_url"
              render={({ field }) => (
                <SocialInput
                  icon="pi pi-linkedin"
                  prefix="linkedin.com"
                  placeholder="/in/handle"
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              control={control}
              name="personal_website_url"
              render={({ field }) => (
                <SocialInput
                  icon="pi pi-globe"
                  placeholder="Your website"
                  value={field.value || ''}
                  onChange={field.onChange}
                />
              )}
            />
          </div>
        </div>

        <div className={styles.avatarSection}>
          <label className={styles.label}>Profile Picture</label>
          <div className={styles.avatarContainer}>
            <div
              className={styles.avatarWrapper}
              onClick={!uploading ? handleAvatarClick : undefined}
              style={{ cursor: uploading ? 'not-allowed' : 'pointer' }}
            >
              <img
                src={avatarUrl}
                alt="Profile"
                className={styles.avatarImage}
                style={{ opacity: uploading ? 0.5 : 1 }}
              />
              <div className={styles.avatarOverlay} style={{ opacity: uploading ? 1 : undefined }}>
                {uploading ? (
                  <i className="pi pi-spin pi-spinner" style={{ fontSize: '32px' }} />
                ) : (
                  <i className="pi pi-upload" style={{ fontSize: '24px' }} />
                )}
              </div>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              disabled={uploading}
            />
            {uploading && <div className={styles.uploadingText}>Uploading...</div>}
          </div>
        </div>
      </div>
    </div>
  );
};
